import React from "react";
import {
  caretDownSx,
  FONT_DISPLAY,
  FONT_UI,
  hairline,
} from "@/src/components/common/broadsheet/tokens";
import { useState } from "react";
import {
  Box,
  Collapse,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListSubheader,
  Typography,
} from "@mui/material";
import JournalDrawerMenuProps from "@/src/_types/journal-drawer-menu-props";
import DrawerLink from "@/src/components/common/_partials/header/drawer-link/drawer-link";
import {
  drawerChildTextSx,
  drawerItemTextSx,
} from "@/src/components/common/_partials/header/drawer-link/drawer-item-styles";

const DrawerMenu = ({
  open,
  toggleDrawer,
  menuItems,
  journalTitle,
  journalSubtitle,
  authorGuide,
  submission,
}: JournalDrawerMenuProps) => {
  const [collapseOpen, setCollapseOpen] = useState<boolean[]>([]);

  const handleToggle = (index: number) => {
    const newCollapseOpen = [...collapseOpen];
    newCollapseOpen[index] = !newCollapseOpen[index];
    setCollapseOpen(newCollapseOpen);
  };

  const allMenuItems = [...menuItems, authorGuide, submission];

  return (
    <Drawer
      anchor="left"
      open={open}
      onClose={() => toggleDrawer(false)}
      slotProps={{
        paper: {
          sx: {
            width: 250,
            backgroundColor: "custom.newsprint",
            backgroundImage: "none",
          },
        },
      }}
    >
      <List
        subheader={
          <ListSubheader
            sx={{
              display: "flex",
              alignItems: "flex-start",
              flexDirection: "column",
              gap: 0.25,
              paddingY: 0.75,
              backgroundColor: "custom.newsprint",
              ...hairline,
            }}
          >
            <Typography
              component="h1"
              sx={{
                color: "custom.ink",
                fontFamily: FONT_DISPLAY,
                fontWeight: 700,
                fontSize: "1.125rem",
                lineHeight: 1.2,
              }}
            >
              {journalTitle}
            </Typography>
            <Typography
              component="h2"
              sx={{
                color: "custom.subtitleText",
                fontFamily: FONT_UI,
                fontSize: "0.71875rem",
                fontWeight: 400,
                lineHeight: 1.4,
              }}
            >
              {journalSubtitle}
            </Typography>
          </ListSubheader>
        }
      >
        {allMenuItems.map((item, index) => (
          <Box key={index}>
            {!item.children ? (
              <DrawerLink
                color="var(--bf-ink)"
                link={item.link || "#"}
                key={index}
              >
                <ListItem sx={{ py: 0.5 }}>
                  <ListItemText primary={item.label} sx={drawerItemTextSx} />
                </ListItem>
              </DrawerLink>
            ) : (
              <>
                <ListItem
                  sx={{
                    color: "custom.ink",
                    py: 0.5,
                    cursor: "pointer",
                  }}
                  onClick={() => handleToggle(index)}
                >
                  <ListItemText primary={item.label} sx={drawerItemTextSx} />
                  <Box
                    component="span"
                    aria-hidden="true"
                    sx={caretDownSx(collapseOpen[index])}
                  />
                </ListItem>
                <Collapse in={collapseOpen[index]} sx={{ ml: 2 }}>
                  <List component="div" disablePadding>
                    {item.children.map((child, childIndex) => (
                      <DrawerLink
                        color="var(--bf-ink-muted)"
                        link={child.link || "#"}
                        key={childIndex}
                      >
                        <ListItem sx={{ py: 0.25 }}>
                          <ListItemText
                            primary={child.label}
                            sx={drawerChildTextSx}
                          />
                        </ListItem>
                      </DrawerLink>
                    ))}
                  </List>
                </Collapse>
              </>
            )}
          </Box>
        ))}
      </List>
    </Drawer>
  );
};

export default DrawerMenu;
